import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { groupsApi } from '../../../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { Users, Share } from 'lucide-react';

const GroupDetailDialog = ({ open, onClose, group, token }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [ setSharedResources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && group) {
      loadSharedResources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group]);

  const loadSharedResources = async () => {
    setIsLoading(true);
    try {
      const response = await groupsApi.getSharedResources(group.id, token);
      setSharedResources(response || []);
    } catch (error) {
      
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!group) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        {group.avatar ? (
          <img 
            src={group.avatar} 
            alt={group.name} 
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <Users className="w-4 h-4 text-blue-500" />
          </div>
        )}
        {group.name}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
          <Tab 
            label={t('dashboard.groups.members')} 
            icon={<Users size={16} />}
            iconPosition="start"
          />
          <Tab 
            label={t('dashboard.groups.shared_resources')} 
            icon={<Share size={16} />}
            iconPosition="start"
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('dashboard.groups.members')} ({group.members?.length || 0})
              </Typography>

              {group.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {group.description}
                  </Typography>
                </Box>
              )}

              <Paper elevation={0} sx={{ border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                {group.members && group.members.length > 0 ? (
                  <List disablePadding>
                    {group.members.map((member, index) => (
                      <React.Fragment key={member.id}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {member.username}
                                {group.owner === member.id && (
                                  <Chip 
                                    label={t('dashboard.groups.owner')} 
                                    size="small" 
                                    color="primary"
                                    sx={{ height: 20 }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={member.email}
                          />
                        </ListItem>
                        {index < group.members.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.groups.no_members')}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('dashboard.groups.shared_resources')}
              </Typography>

              {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Alert severity="info">
                  {t('dashboard.groups.shared_resources_coming_soon')}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Button onClick={onClose} variant="outlined">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupDetailDialog;
